import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSupervisor } from '@/hooks/useSupervisor';
import { CanvasStepRibbon, type CanvasStepDefinition, type CanvasStepStatus } from './CanvasStepRibbon';
import { requestStepLeave } from '@/services/stepLeaveGuard';
import { CANVAS_STEP_CONFIG } from './stepConfig';
import type { Order, OrderStep } from '@/types/supervisor';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  selectOrderItems,
  selectSelectedItemIds,
  updateOrderItem,
  createOrderForCheckout,
  syncOrderForCheckout,
  type OrderItem,
  selectShippingAddress,
} from '@/features/order/orderSlice';
import { useOrderAiPrompts, type MissingField } from '@/components/order/useSelectionHelpPrompt';
import { logging } from '@/services/logging';
import { isOrderFlowItemStatus } from '@/types/orderStatus';
import { isShippingAddressComplete } from '@/services/supervisorService';

type SupervisorOrderItem = Order['items'][number] & { id: string };
type CanonicalOrderItem = OrderItem & { id: string };
type OrderItemLike = SupervisorOrderItem | CanonicalOrderItem;

const isOrderedStatus = (value: unknown): boolean => isOrderFlowItemStatus(value);

export interface ReviewStepPreconditionParams {
  readonly selectableItems: readonly (OrderItem & { id: string })[];
  readonly selectedItemIds: readonly string[];
  readonly findOrderItem: (orderItemId: string | null | undefined) => OrderItemLike | null;
  readonly buildMissingFields: (item: OrderItemLike | null) => MissingField[];
}

export type ReviewStepPreconditionResult =
  | { outcome: 'noSelectableItems' }
  | { outcome: 'noValidSelection' }
  | { outcome: 'missingConfiguration'; missingFields: MissingField[] }
  | { outcome: 'singleReady'; itemId: string }
  | { outcome: 'multiReady'; orderItemIds: string[] };

export const evaluateReviewStepPreconditions = (
  params: ReviewStepPreconditionParams,
): ReviewStepPreconditionResult => {
  const { selectableItems, selectedItemIds, findOrderItem, buildMissingFields } = params;

  if (selectableItems.length === 0) {
    return { outcome: 'noSelectableItems' };
  }

  if (selectableItems.length === 1) {
    const [single] = selectableItems;
    const mergedItem = findOrderItem(single.id) ?? single;
    const missing = buildMissingFields(mergedItem);
    if (missing.length > 0) {
      return { outcome: 'missingConfiguration', missingFields: missing };
    }
    return { outcome: 'singleReady', itemId: single.id };
  }

  const selectableSet = new Set(selectableItems.map(item => item.id));
  const validSelections = selectedItemIds.filter(
    (id): id is string => typeof id === 'string' && selectableSet.has(id),
  );

  if (validSelections.length === 0) {
    return { outcome: 'noValidSelection' };
  }

  const missingForSelections = new Set<MissingField>();
  for (const orderItemId of validSelections) {
    const candidate = findOrderItem(orderItemId);
    const missing = buildMissingFields(candidate);
    missing.forEach(field => missingForSelections.add(field));
  }

  if (missingForSelections.size > 0) {
    return { outcome: 'missingConfiguration', missingFields: Array.from(missingForSelections) };
  }

  return { outcome: 'multiReady', orderItemIds: validSelections };
};

const CanvasStepRibbonIndicator = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { state: supervisorState, send: sendToSupervisor } = useSupervisor();
  const orderItems = useAppSelector(selectOrderItems) ?? [];
  const selectedItemIds = useAppSelector(selectSelectedItemIds) ?? [];
  const orderId = useAppSelector((state) => state.order.id ?? null);
  const shippingAddress = useAppSelector(selectShippingAddress);

  const supervisorContextOrder = supervisorState.context.order;

  const supervisorItemById = useMemo(() => {
    const map = new Map<string, SupervisorOrderItem>();
    const items = supervisorContextOrder?.items;
    if (!Array.isArray(items)) {
      return map;
    }
    for (const entry of items) {
      if (entry && typeof entry.id === 'string' && entry.id.length > 0) {
        map.set(entry.id, entry as SupervisorOrderItem);
      }
    }
    return map;
  }, [supervisorContextOrder]);

  const orderItemById = useMemo(() => {
    const map = new Map<string, CanonicalOrderItem>();
    for (const entry of orderItems) {
      if (entry && typeof entry.id === 'string' && entry.id.length > 0) {
        map.set(entry.id, entry as CanonicalOrderItem);
      }
    }
    return map;
  }, [orderItems]);

  const canonicalSupervisorItems = useMemo(() => {
    const merged: SupervisorOrderItem[] = [];

    for (const [id, storeItem] of orderItemById.entries()) {
      const fallback = supervisorItemById.get(id);
      const mergedEntry: SupervisorOrderItem = {
        ...(fallback ?? {}),
        ...storeItem,
        id,
        status: storeItem.status ?? fallback?.status ?? 'NEW',
        orderId: storeItem.orderId ?? fallback?.orderId ?? null,
        uploadedFileId: storeItem.uploadedFileId ?? fallback?.uploadedFileId,
        paperTypeId: storeItem.paperTypeId ?? fallback?.paperTypeId,
        printerId: storeItem.printerId ?? fallback?.printerId,
        widthIn:
          typeof storeItem.widthIn === 'number' && Number.isFinite(storeItem.widthIn)
            ? storeItem.widthIn
            : fallback?.widthIn,
        heightIn:
          typeof storeItem.heightIn === 'number' && Number.isFinite(storeItem.heightIn)
            ? storeItem.heightIn
            : fallback?.heightIn,
        marginIn: storeItem.marginIn ?? fallback?.marginIn,
        galleryWrapIn: storeItem.galleryWrapIn ?? fallback?.galleryWrapIn,
        colorMode: storeItem.colorMode ?? fallback?.colorMode,
        price: storeItem.price ?? fallback?.price,
        pricingStatus: storeItem.pricingStatus ?? fallback?.pricingStatus,
        pricingError: storeItem.pricingError ?? fallback?.pricingError ?? null,
        originalWidthPx: storeItem.originalWidthPx ?? fallback?.originalWidthPx,
        originalHeightPx: storeItem.originalHeightPx ?? fallback?.originalHeightPx,
        originalWidthIn: storeItem.originalWidthIn ?? fallback?.originalWidthIn,
        originalHeightIn: storeItem.originalHeightIn ?? fallback?.originalHeightIn,
        dpiX: storeItem.dpiX ?? fallback?.dpiX,
        dpiY: storeItem.dpiY ?? fallback?.dpiY,
        createdAt:
          typeof storeItem.createdAt === 'string'
            ? storeItem.createdAt
            : fallback?.createdAt,
        updatedAt:
          typeof storeItem.updatedAt === 'string'
            ? storeItem.updatedAt
            : fallback?.updatedAt,
      };
      merged.push(mergedEntry);
    }

    for (const [id, fallback] of supervisorItemById.entries()) {
      if (!orderItemById.has(id)) {
        merged.push(fallback);
      }
    }

    return merged;
  }, [orderItemById, supervisorItemById]);

  const hasUploadedItems = useMemo(() => {
    return orderItems.length > 0;
  }, [orderItems]);

  const sizeProgress = useMemo(() => {
    if (orderItems.length === 0) {
      return 'upcoming' as CanvasStepStatus;
    }
    const total = orderItems.length;
    const configured = orderItems.filter(item => {
      return typeof item?.widthIn === 'number' && item.widthIn! > 0 && typeof item?.heightIn === 'number' && item.heightIn! > 0;
    }).length;
    if (configured === 0) {
      return 'upcoming' as CanvasStepStatus;
    }
    if (configured === total) {
      return 'complete' as CanvasStepStatus;
    }
    return 'partial' as CanvasStepStatus;
  }, [orderItems]);

  const paperProgress = useMemo(() => {
    if (orderItems.length === 0) {
      return 'upcoming' as CanvasStepStatus;
    }
    const total = orderItems.length;
    const configured = orderItems.filter(item => typeof item?.paperTypeId === 'string' && item.paperTypeId!.length > 0).length;
    if (configured === 0) {
      return 'upcoming' as CanvasStepStatus;
    }
    if (configured === total) {
      return 'complete' as CanvasStepStatus;
    }
    return 'partial' as CanvasStepStatus;
  }, [orderItems]);

  const printerProgress = useMemo(() => {
    if (orderItems.length === 0) {
      return 'upcoming' as CanvasStepStatus;
    }
    const total = orderItems.length;
    const configured = orderItems.filter(item => typeof item?.printerId === 'string' && item.printerId!.length > 0).length;
    if (configured === 0) {
      return 'upcoming' as CanvasStepStatus;
    }
    if (configured === total) {
      return 'complete' as CanvasStepStatus;
    }
    return 'partial' as CanvasStepStatus;
  }, [orderItems]);

  const orderProgress = useMemo(() => {
    if (orderItems.length === 0) {
      return 'upcoming' as CanvasStepStatus;
    }
    const total = orderItems.length;
    const ordered = orderItems.filter(item => isOrderedStatus(item?.status)).length;
    if (ordered === 0) {
      return 'upcoming' as CanvasStepStatus;
    }
    if (ordered === total) {
      return 'complete' as CanvasStepStatus;
    }
    return 'partial' as CanvasStepStatus;
  }, [orderItems]);

  const sizeRemainingCount = useMemo(() => {
    if (orderItems.length === 0) {
      return 0;
    }
    return orderItems.filter(item => {
      const widthIn = typeof item?.widthIn === 'number' ? item.widthIn : undefined;
      const heightIn = typeof item?.heightIn === 'number' ? item.heightIn : undefined;
      return !(typeof widthIn === 'number' && widthIn > 0 && typeof heightIn === 'number' && heightIn > 0);
    }).length;
  }, [orderItems]);

  const paperRemainingCount = useMemo(() => {
    if (orderItems.length === 0) {
      return 0;
    }
    return orderItems.filter(item => !(typeof item?.paperTypeId === 'string' && item.paperTypeId.length > 0)).length;
  }, [orderItems]);

  const printerRemainingCount = useMemo(() => {
    if (orderItems.length === 0) {
      return 0;
    }
    return orderItems.filter(item => !(typeof item?.printerId === 'string' && item.printerId.length > 0)).length;
  }, [orderItems]);

  const orderRemainingCount = useMemo(() => {
    if (orderItems.length === 0) {
      return 0;
    }
    const ordered = orderItems.filter(item => isOrderedStatus(item?.status)).length;
    return Math.max(0, orderItems.length - ordered);
  }, [orderItems]);

  const addressProgress = useMemo(() => {
    const machineAddress = supervisorState.context.order?.shippingAddress;
    const effectiveAddress = machineAddress ?? shippingAddress;
    return isShippingAddressComplete(effectiveAddress) ? 'complete' : 'upcoming';
  }, [shippingAddress, supervisorState.context.order?.shippingAddress]);

  const orderedItems = useMemo(
    () =>
      orderItems.filter((item): item is OrderItem & { id: string } => {
        return isOrderedStatus(item?.status) && typeof item?.id === 'string' && item.id.length > 0;
      }),
    [orderItems],
  );

  const hasOrderedItems = orderedItems.length > 0;

  const findOrderItem = useCallback(
    (orderItemId: string | null | undefined): OrderItemLike | null => {
      if (!orderItemId) {
        return null;
      }
      const fromStore = orderItemById.get(orderItemId);
      if (fromStore) {
        return fromStore;
      }
      return supervisorItemById.get(orderItemId) ?? null;
    },
    [orderItemById, supervisorItemById],
  );

  const buildMissingFields = useCallback((item: OrderItemLike | null): MissingField[] => {
    if (!item) {
      return ['size', 'paper', 'printer'];
    }

    const widthIn = typeof item.widthIn === 'number' ? item.widthIn : undefined;
    const heightIn = typeof item.heightIn === 'number' ? item.heightIn : undefined;
    const paperTypeId = typeof item.paperTypeId === 'string' ? item.paperTypeId : undefined;
    const printerId = typeof item.printerId === 'string' ? item.printerId : undefined;

    const missing: MissingField[] = [];
    if (!(typeof widthIn === 'number' && widthIn > 0 && typeof heightIn === 'number' && heightIn > 0)) {
      missing.push('size');
    }
    if (!(typeof paperTypeId === 'string' && paperTypeId.length > 0)) {
      missing.push('paper');
    }
    if (!(typeof printerId === 'string' && printerId.length > 0)) {
      missing.push('printer');
    }
    return missing;
  }, [orderItemById]);

  const { sendMissingConfigurationPrompt } = useOrderAiPrompts(sendToSupervisor);

  const handleMissingConfiguration = useCallback((missing: MissingField[]) => {
    if (missing.length === 0) {
      return;
    }

    sendMissingConfigurationPrompt(missing);
  }, [sendMissingConfigurationPrompt]);

  type AddAttemptStatus = 'added' | 'alreadyOrdered' | 'blocked';

  const attemptAddToOrder = useCallback((orderItemId: string | null | undefined): AddAttemptStatus => {
    if (!orderItemId) {
      return 'blocked';
    }
    const item = findOrderItem(orderItemId);
    if (!item?.id) {
      return 'blocked';
    }

    const storeItem = orderItemById.get(item.id);
    const effectiveStatus = (storeItem?.status ?? item.status) ?? undefined;
    if (isOrderedStatus(effectiveStatus)) {
      return 'alreadyOrdered';
    }

    const missing = buildMissingFields(storeItem ?? item);
    if (missing.length > 0) {
      handleMissingConfiguration(missing);
      return 'blocked';
    }

    const currentOrderId = orderId ?? undefined;
    const updatePayload = currentOrderId
      ? { status: 'IN_ORDER' as const, orderId: currentOrderId }
      : { status: 'IN_ORDER' as const };

    dispatch(updateOrderItem({
      orderItemId: item.id,
      data: updatePayload,
    }));
    return 'added';
  }, [buildMissingFields, dispatch, findOrderItem, handleMissingConfiguration, orderId, orderItemById]);

  const applySupervisorStatusPatch = useCallback((orderedIds: readonly string[]) => {
    if (orderedIds.length === 0 || canonicalSupervisorItems.length === 0) {
      return;
    }

    const orderedSet = new Set(orderedIds);
    const nextItems = canonicalSupervisorItems.map(item => {
      if (!item || typeof item.id !== 'string') {
        return item;
      }
      if (!orderedSet.has(item.id)) {
        return item;
      }
      return {
        ...item,
        status: 'IN_ORDER' as const,
      };
    }).filter((item): item is SupervisorOrderItem => Boolean(item?.id));

    sendToSupervisor({
      type: 'ORDER.APPLY_PATCH',
      patch: {
        items: nextItems,
      },
    });
  }, [canonicalSupervisorItems, sendToSupervisor]);

  const steps: readonly CanvasStepDefinition[] = useMemo(() => {
    const activeStep = supervisorState.context.activeStep;
    return CANVAS_STEP_CONFIG.map((step, index) => {
      const label = t(step.labelKey, step.defaultLabel);
      let status: CanvasStepStatus = 'upcoming';
      const isActive = step.id === activeStep;

      if (step.id === 'upload') {
        if (!activeStep) {
          status = 'current';
        } else {
          status = hasUploadedItems ? 'complete' : 'upcoming';
        }
      } else if (step.id === 'uploadedImages') {
        status = hasUploadedItems ? 'complete' : 'upcoming';
      } else if (step.id === 'size') {
        status = sizeProgress;
      } else if (step.id === 'paper') {
        status = paperProgress;
      } else if (step.id === 'printer') {
        status = printerProgress;
      } else if (step.id === 'review') {
        status = orderProgress;
      } else if (step.id === 'address') {
        status = addressProgress;
      } else if (step.id === 'payment' || step.id === 'done') {
        status = 'upcoming';
      } else if (step.id === 'frame') {
        status = orderProgress;
      }

      if (isActive) {
        status = 'current';
      } else {
        const activeIndex = CANVAS_STEP_CONFIG.findIndex(config => config.id === activeStep);
        if (activeIndex >= 0) {
          const stepIndex = index;
          if (stepIndex < activeIndex) {
            if (status === 'upcoming' && activeStep !== 'done' && activeStep !== 'payment') {
              status = 'complete';
            }
          } else if (stepIndex > activeIndex && status === 'current') {
            status = 'upcoming';
          }
        }
      }

      return {
        id: step.id,
        label,
        status,
        disabled: step.id === 'size' && !hasUploadedItems,
        meta:
          step.id === 'size'
            ? { remainingCount: sizeRemainingCount }
            : step.id === 'paper'
              ? { remainingCount: paperRemainingCount }
              : step.id === 'printer'
                ? { remainingCount: printerRemainingCount }
                : step.id === 'review'
                  ? { remainingCount: orderRemainingCount }
                  : undefined,
      } satisfies CanvasStepDefinition;
    });
  }, [
    addressProgress,
    hasUploadedItems,
    orderProgress,
    orderRemainingCount,
    paperProgress,
    paperRemainingCount,
    printerProgress,
    printerRemainingCount,
    sizeProgress,
    sizeRemainingCount,
    supervisorState.context.activeStep,
    t,
  ]);

  const handleStepSelect = useCallback(async (step: CanvasStepDefinition) => {
    const { id: stepId } = step;
    const canLeaveCurrentStep = await requestStepLeave({
      source: 'stepRibbon',
      targetStep: stepId as OrderStep,
    });
    if (!canLeaveCurrentStep) {
      return;
    }

    const idsToMarkOrdered: string[] = [];

    if (stepId === 'size' && !hasUploadedItems) {
      const message = t('canvas.steps.sizePrereq', 'Upload at least one image before setting the print size.');
      if (typeof window !== 'undefined') {
        window.alert(message);
      }
      return;
    }

    if ((stepId === 'address' || stepId === 'payment') && !hasOrderedItems) {
      const fallbackMessage = t(
        'uploadedImages.orderSelectionPrompt',
        'Select the images you want to include in your order, then press “Order” again.',
      );
      sendToSupervisor({
        type: 'UI.REQUEST_AI_PROMPT',
        aiPrompt: 'Politely explain that the user needs to add at least one configured image to the order before providing shipping details or proceeding to payment.',
        fallbackMessage,
      });
      return;
    }

    if (stepId === 'review') {
      if (orderedItems.length > 0) {
        const missingForOrdered = new Set<MissingField>();
        for (const orderedItem of orderedItems) {
          const missing = buildMissingFields(orderedItem);
          missing.forEach(field => missingForOrdered.add(field));
        }

        if (missingForOrdered.size > 0) {
          handleMissingConfiguration(Array.from(missingForOrdered));
        }
      }

      const selectableItems = orderItems.filter((item): item is OrderItem & { id: string } => Boolean(item?.id));

      const preconditionResult = evaluateReviewStepPreconditions({
        selectableItems,
        selectedItemIds,
        findOrderItem,
        buildMissingFields,
      });

      if (preconditionResult.outcome === 'noSelectableItems') {
        if (hasOrderedItems) {
          sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'review' });
          return;
        }

        const fallbackMessage = t(
          'uploadedImages.orderSelectionPrompt',
          'Select the images you want to include in your order, then press “Order” again.',
        );
        sendToSupervisor({
          type: 'UI.REQUEST_AI_PROMPT',
          aiPrompt: 'Politely explain that the user needs to select at least one uploaded image before placing an order.',
          fallbackMessage,
        });
        return;
      }

      if (preconditionResult.outcome === 'noValidSelection') {
        if (hasOrderedItems) {
          sendToSupervisor({ type: 'UI.GOTO_STEP', step: 'review' });
          return;
        }

        const fallbackMessage = t(
          'uploadedImages.orderSelectionPrompt',
          'Select the images you want to include in your order, then press “Order” again.',
        );
        sendToSupervisor({
          type: 'UI.REQUEST_AI_PROMPT',
          aiPrompt: 'Politely explain that the user needs to select at least one uploaded image before placing an order.',
          fallbackMessage,
        });
        return;
      }

      if (preconditionResult.outcome === 'missingConfiguration') {
        handleMissingConfiguration(preconditionResult.missingFields);
        return;
      }

      if (preconditionResult.outcome === 'singleReady') {
        const status = attemptAddToOrder(preconditionResult.itemId);
        if (status === 'blocked') {
          return;
        }
        idsToMarkOrdered.push(preconditionResult.itemId);
      }

      if (preconditionResult.outcome === 'multiReady') {
        const validSelections = preconditionResult.orderItemIds;

        const results = validSelections.map(orderItemId => ({
          orderItemId,
          status: attemptAddToOrder(orderItemId),
        }));

        const addedCount = results.filter(result => result.status === 'added').length;
        const alreadyCount = results.filter(result => result.status === 'alreadyOrdered').length;

        if (validSelections.length > 1 && (addedCount > 0 || alreadyCount > 0)) {
          const parts: string[] = [];

          parts.push(
            t('uploadedImages.multiAddSummary.intro', {
              total: validSelections.length,
              added: addedCount,
              already: alreadyCount,
            }),
          );

          if (addedCount > 0) {
            const addedKey = addedCount === 1
              ? 'uploadedImages.multiAddSummary.addedOne'
              : 'uploadedImages.multiAddSummary.addedMany';
            parts.push(t(addedKey, { count: addedCount }));
          }

          if (alreadyCount > 0) {
            const alreadyKey = alreadyCount === 1
              ? 'uploadedImages.multiAddSummary.alreadyOne'
              : 'uploadedImages.multiAddSummary.alreadyMany';
            parts.push(t(alreadyKey, { count: alreadyCount }));
          }

          const summaryMessage = parts.join(' ');
          sendToSupervisor({ type: 'UI.ANNOUNCE_UPLOAD_SUMMARY', summaryText: summaryMessage });
        }

        const succeededIds = results
          .filter(result => result.status === 'added' || result.status === 'alreadyOrdered')
          .map(result => result.orderItemId);
        idsToMarkOrdered.push(...succeededIds);
      }
    }

    if (idsToMarkOrdered.length > 0) {
      applySupervisorStatusPatch(idsToMarkOrdered);
    }

    if (stepId === 'payment') {
      try {
        if (!orderId) {
          await dispatch(createOrderForCheckout()).unwrap();
        } else {
          await dispatch(syncOrderForCheckout()).unwrap();
        }
      } catch (error) {
        logging.error('Failed to prepare order before payment step', { error });
        return;
      }
    }

    sendToSupervisor({ type: 'UI.GOTO_STEP', step: stepId as OrderStep });
  }, [
    applySupervisorStatusPatch,
    attemptAddToOrder,
    buildMissingFields,
    dispatch,
    findOrderItem,
    handleMissingConfiguration,
    hasOrderedItems,
    hasUploadedItems,
    orderId,
    orderItems,
    orderedItems,
    selectedItemIds,
    sendToSupervisor,
    syncOrderForCheckout,
    t,
  ]);

  return (
    <div className="w-full md:w-auto md:ml-auto">
      <CanvasStepRibbon
        steps={steps}
        onStepSelect={handleStepSelect}
      />
    </div>
  );
};

export default CanvasStepRibbonIndicator;
